import { preferAcknowledgedActivity } from './useWorkoutStart';

// One entry per workout started in this mounted account scope. Keep the latest
// canonical version fence even after confirmation: an older concurrent GET may
// still finish later. No timers, cross-account cache, polling or persisted data.
export function createWorkoutStartReconciliation() {
  const acknowledged = new Map();
  const operations = new Map();
  const deleted = new Set();
  const mutations = new Map();
  let mutationSequence = 0;
  let active = true;
  return {
    activate() {
      active = true;
      for (const [id, scope] of operations) scope.active = !deleted.has(id);
    },
    isActive: () => active,
    snapshot: () => mutationSequence,
    // Canonical non-workout receipts fence only reads already in flight, not
    // future manual edits/reopens which may have no lifecycle revision.
    acknowledgeMutation(row) {
      if (!row?._id || deleted.has(String(row._id))) return null;
      mutations.set(String(row._id), { row, sequence: ++mutationSequence });
      return row;
    },
    dispose() {
      active = false;
      for (const scope of operations.values()) { scope.active = false; scope.generation += 1; }
    },
    operation(id, create) {
      const key = String(id);
      if (!operations.has(key)) operations.set(key, create());
      return operations.get(key);
    },
    complete(id, row) {
      const key = String(id);
      if (deleted.has(key)) return null;
      const known = row || acknowledged.get(key);
      const completed = known ? { ...known, status: 'complete' } : null;
      const scope = operations.get(key);
      if (scope) {
        scope.generation += 1;
        scope.checkingRef.current = false;
        // The parent completion callback owns the visible transition; keep
        // the editor mounted through its existing finishing stage.
        scope.phase = 'idle'; scope.notice = null;
        scope.listeners.forEach(listener => listener());
      }
      if (completed) acknowledged.set(key, completed);
      return completed;
    },
    acknowledge(row) {
      if (!row?._id || deleted.has(String(row._id))) return null;
      const merged = preferAcknowledgedActivity(row, acknowledged.get(String(row._id)));
      acknowledged.set(String(row._id), merged);
      return merged;
    },
    has: id => acknowledged.has(String(id)),
    isDeleted: id => deleted.has(String(id)),
    forget(id) {
      const key = String(id);
      deleted.add(key);
      acknowledged.delete(key);
      mutations.delete(key);
      const scope = operations.get(key);
      if (scope) { scope.active = false; scope.generation += 1; }
    },
    reconcile(rows, readSequence = mutationSequence) {
      const mutationSeen = new Set();
      rows = rows.map(row => {
        const id = String(row._id);
        mutationSeen.add(id);
        const receipt = mutations.get(id);
        if (!receipt) return row;
        if (receipt.sequence > readSequence) return receipt.row;
        receipt.row = row;
        return row;
      });
      for (const [id, receipt] of mutations) {
        if (!mutationSeen.has(id) && receipt.sequence > readSequence) rows.push(receipt.row);
      }
      const seen = new Set();
      const result = rows.filter(row => !deleted.has(String(row._id))).map(row => {
        const id = String(row._id); seen.add(id);
        if (!acknowledged.has(id)) return row;
        const merged = preferAcknowledgedActivity(row, acknowledged.get(id));
        acknowledged.set(id, merged);
        return merged;
      });
      // A pre-start filtered read can omit the occurrence entirely. Retain the
      // acknowledged row rather than making the initiating editor disappear.
      for (const [id, row] of acknowledged) if (!seen.has(id)) result.push(row);
      return result;
    },
  };
}
